import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  FilmStrip, 
  CreditCard, 
  Plus, 
  Pencil, 
  Trash, 
  X,
  Check,
  MagnifyingGlass,
  CaretDown,
  Database,
  Tag,
  UploadSimple,
  FileText,
  Warning,
  ShieldWarning,
  ShieldCheck,
  Eye,
  UserMinus,
  XCircle,
  CheckCircle,
  Flag,
  Robot,
  ArrowsClockwise,
  RedditLogo,
  ShareNetwork,
  Trophy,
  Crown,
  Heartbeat,
  ArrowClockwise,
  Megaphone,
  ChatCenteredDots,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import AdminGalleryViewer from '../components/admin/AdminGalleryViewer';
import BroadcastsTab from '../components/admin/BroadcastsTab';
import FeedbackAdminTab from '../components/admin/FeedbackAdminTab';
import UsersTab from '../components/admin/UsersTab';
import PaymentsTab from '../components/admin/PaymentsTab';
import DealsTab from '../components/admin/DealsTab';
import PayoutsTab from '../components/admin/PayoutsTab';
import SweepConfirmModal from '../components/admin/SweepConfirmModal';
import HorizontalScroller from '../components/common/HorizontalScroller';
import HealthTab from '../components/admin/HealthTab';
import AffiliatesTab from '../components/admin/AffiliatesTab';
import ContentReviewTab from '../components/admin/ContentReviewTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import ModerationTab from '../components/admin/ModerationTab';
import ActivityLogTab from '../components/admin/ActivityLogTab';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('content');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Gallery viewer
  const [galleryIndex, setGalleryIndex] = useState(null);

  // Content Review (pre-publication moderation) — only the badge count remains
  // in AdminPage. The list + actions live inside <ContentReviewTab />.
  const [pendingContentCount, setPendingContentCount] = useState(0);
  const [importMode, setImportMode] = useState('json'); // 'json' or 'csv'
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Moderation / Reports state now lives inside <ModerationTab />. We keep a
  // lightweight `modStats` here just to populate the sidebar badge + the
  // top-level "Pending Reports" stat tile. ModerationTab calls onActionTaken
  // (= refreshModStats) after any action so the badge stays in sync.
  const [modStats, setModStats] = useState(null);

  const refreshModStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/moderation/stats`, { withCredentials: true });
      setModStats(res.data);
      fetchStats();
    } catch { /* silent */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [aggregating, setAggregating] = useState(false);
  const [aggResult, setAggResult] = useState(null);
  const [aggCategories, setAggCategories] = useState([]);
  const [aggSort, setAggSort] = useState('hot');
  const [aggLimit, setAggLimit] = useState(25);
  const [autoStatus, setAutoStatus] = useState(null);
  const [contentSources, setContentSources] = useState(null);
  const [mastodonAggregating, setMastodonAggregating] = useState(false);
  const [twitterUrls, setTwitterUrls] = useState('');
  const [twitterImporting, setTwitterImporting] = useState(false);
  const [twitterCategory, setTwitterCategory] = useState('amateur');

  // Affiliates / Health state moved into their respective components.

  const [formData, setFormData] = useState({
    title: '',
    thumbnail_url: '',
    source_url: '',
    source_name: '',
    duration: '',
    views: 0,
    tags: '',
    content_type: 'video',
    is_free: true,
    tier: 'free',
    category: 'amateur',
  });

  // categoryForm previously lived here for the in-page Add-Category dialog,
  // which is now inside <CategoriesTab />. Form state removed.

  // Keep pendingContentCount in sync (for the sidebar badge) even when the
  // user isn't on the review tab. ContentReviewTab self-fetches when active.
  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/content/pending`, { withCredentials: true });
      setPendingContentCount(res.data.total || (res.data.items?.length || 0));
    } catch { /* silent */ }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/external-content`, { withCredentials: true });
      setContent(response.data.items);
    } catch (err) {
      console.error('Failed to fetch content');
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  }, []);

  const fetchPendingContent = fetchPendingCount;  // legacy alias used by handleSubmit/handleEdit success paths

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchContent(), fetchCategories(), refreshModStats(), fetchPendingCount()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchContent, fetchCategories, refreshModStats, fetchPendingCount]);

  // Fetch reports when filter changes or tab switches.
  // Moderation / Affiliates / Health are self-loading inside their own components.
  // No tab-switch effect needed here.

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail_url: '',
      source_url: '',
      source_name: '',
      duration: '',
      views: 0,
      tags: '',
      content_type: 'video',
      is_free: true,
      tier: 'free',
      category: 'amateur',
    });
    setEditingItem(null);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.thumbnail_url || !formData.source_url) {
      toast.error('Title, thumbnail URL, and source URL are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        views: parseInt(formData.views) || 0,
      };

      if (editingItem) {
        await axios.put(
          `${API}/api/admin/external-content/${editingItem.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Content updated');
      } else {
        await axios.post(
          `${API}/api/admin/external-content`,
          payload,
          { withCredentials: true }
        );
        toast.success('Content created');
      }

      resetForm();
      setIsAddOpen(false);
      fetchContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      thumbnail_url: item.thumbnail_url || '',
      source_url: item.source_url || '',
      source_name: item.source_name || '',
      duration: item.duration || '',
      views: item.views || 0,
      tags: (item.tags || []).join(', '),
      content_type: item.content_type || 'video',
      is_free: item.is_free !== false,
      tier: item.tier || 'free',
      category: item.category || 'amateur',
    });
    setIsAddOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      await axios.delete(`${API}/api/admin/external-content/${id}`, { withCredentials: true });
      toast.success('Content deleted');
      fetchContent();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSeedContent = async () => {
    try {
      const response = await axios.post(`${API}/api/admin/seed-content`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchContent();
      fetchCategories();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed content');
    }
  };

  const handleBulkImport = async () => {
    if (!importData.trim()) {
      toast.error('Please enter data to import');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      let response;
      
      if (importMode === 'json') {
        // Parse and validate JSON
        let items;
        try {
          items = JSON.parse(importData);
          if (!Array.isArray(items)) {
            items = [items]; // Allow single object
          }
        } catch (e) {
          toast.error('Invalid JSON format');
          setImporting(false);
          return;
        }

        response = await axios.post(
          `${API}/api/admin/bulk-import`,
          { items, skip_duplicates: true },
          { withCredentials: true }
        );
      } else {
        // CSV import
        response = await axios.post(
          `${API}/api/admin/bulk-import-csv`,
          { csv_data: importData, skip_duplicates: true },
          { withCredentials: true }
        );
      }

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} items`);
        fetchContent();
        fetchStats();
      } else if (response.data.skipped > 0) {
        toast.info(`All ${response.data.skipped} items were duplicates (skipped)`);
      } else {
        toast.warning('No items were imported');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClearAllContent = async () => {
    if (!window.confirm('Are you sure you want to DELETE ALL external content? This cannot be undone!')) return;
    if (!window.confirm('FINAL WARNING: This will permanently delete ALL external content. Continue?')) return;

    try {
      const response = await axios.delete(`${API}/api/admin/external-content/clear-all`, { withCredentials: true });
      toast.success(response.data.message);
      fetchContent();
      fetchStats();
    } catch (err) {
      toast.error('Failed to clear content');
    }
  };

  // ── Compliance Sweep: deletes external_content items lacking proper attribution
  const [sweepDryRun, setSweepDryRun] = useState(null);   // result of the dry-run
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sweepShowConfirm, setSweepShowConfirm] = useState(false);

  const runComplianceSweep = async (dryRun = true) => {
    setSweepRunning(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/external-content/sweep-unattributed?dry_run=${dryRun}`,
        {},
        { withCredentials: true },
      );
      if (dryRun) {
        setSweepDryRun(res.data);
        setSweepShowConfirm(true);
      } else {
        toast.success(`Sweep complete: ${res.data.deleted} items permanently removed.`, { duration: 8000 });
        setSweepDryRun(null);
        setSweepShowConfirm(false);
        fetchContent();
        fetchStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweepRunning(false);
    }
  };

  // handleAddCategory / handleDeleteCategory / isCategoryOpen now live in <CategoriesTab />.

  // handleModerationAction now lives in <ModerationTab />.

  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(`${API}/api/admin/moderation/unban/${userId}`, {}, { withCredentials: true });
      toast.success('User unbanned');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unban user');
    }
  };

  const handleRunAggregator = async () => {
    setAggregating(true);
    setAggResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/aggregator/run?limit=${aggLimit}&sort=${aggSort}`,
        {},
        { withCredentials: true }
      );
      const jobId = res.data.job_id;
      toast.success('Aggregation started! Fetching content from Reddit...');

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/api/admin/aggregator/status/${jobId}`, { withCredentials: true });
          const job = statusRes.data;

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            setAggResult({
              new_items: job.result.total_new,
              duplicates_skipped: job.result.total_skipped,
              by_category: job.result.by_category,
            });
            setAggregating(false);
            toast.success(`Done! Imported ${job.result.total_new} new items`);
            fetchContent();
            fetchStats();
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setAggregating(false);
            toast.error(`Aggregation failed: ${job.error || 'Unknown error'}`);
          }
        } catch (err) {
          // Keep polling
        }
      }, 5000);

      // Safety timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (aggregating) {
          setAggregating(false);
          toast.error('Aggregation timed out. Check results later.');
        }
      }, 300000);

    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start aggregation');
      setAggregating(false);
    }
  };

  const fetchAggCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/aggregator/categories`, { withCredentials: true });
      setAggCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch aggregator categories');
    }
  }, []);

  const fetchAutoStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/aggregator/auto-status`, { withCredentials: true });
      setAutoStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch auto-aggregation status');
    }
  }, []);

  const fetchContentSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/aggregator/sources`, { withCredentials: true });
      setContentSources(res.data);
    } catch (err) {
      console.error('Failed to fetch content sources');
    }
  }, []);

  const runMastodonAggregator = async () => {
    setMastodonAggregating(true);
    try {
      await axios.post(`${API}/api/admin/aggregator/mastodon/run?limit=20`, {}, { withCredentials: true });
      toast.success('Mastodon aggregation started!');
      // Poll for completion
      let attempts = 0;
      const checkInterval = setInterval(async () => {
        attempts++;
        try {
          const res = await axios.get(`${API}/api/admin/aggregator/mastodon/status`, { withCredentials: true });
          if (res.data.last_run?.status === 'completed' && attempts > 2) {
            clearInterval(checkInterval);
            setMastodonAggregating(false);
            toast.success(`Done! ${res.data.total_mastodon_content} total Mastodon items`);
            fetchContentSources();
            fetchStats();
          }
        } catch (e) { /* ignore polling errors */ }
        if (attempts > 30) {
          clearInterval(checkInterval);
          setMastodonAggregating(false);
        }
      }, 5000);
    } catch (err) {
      toast.error('Failed to start Mastodon aggregation');
      setMastodonAggregating(false);
    }
  };

  const importTwitterContent = async () => {
    const urls = twitterUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) {
      toast.error('Paste at least one tweet URL');
      return;
    }
    setTwitterImporting(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/aggregator/twitter/import`,
        { urls, category: twitterCategory },
        { withCredentials: true }
      );
      toast.success(`Imported ${res.data.total_imported}, skipped ${res.data.total_skipped}, failed ${res.data.total_failed}`);
      setTwitterUrls('');
      fetchContentSources();
      fetchStats();
    } catch (err) {
      toast.error('Twitter import failed');
    } finally {
      setTwitterImporting(false);
    }
  };

  const filteredContent = content.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.source_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400">Admin access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Admin Panel</h1>
          <p className="text-slate-400">Manage content and platform settings</p>
        </div>
      </div>

      {/* Stats Cards — clickable */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <button onClick={() => setActiveTab('users')} className="glass-light rounded-xl p-4 text-left hover:ring-2 hover:ring-blue-500/30 transition-all">
            <Users className="w-8 h-8 text-blue-500 mb-2" />
            <div className="text-2xl font-bold text-white">{stats.total_users}</div>
            <div className="text-sm text-slate-400">Total Users</div>
          </button>
          <button onClick={() => { setActiveTab('users'); setUserRoleFilter('creator'); }} className="glass-light rounded-xl p-4 text-left hover:ring-2 hover:ring-amber-500/30 transition-all">
            <Users className="w-8 h-8 text-amber-400 mb-2" />
            <div className="text-2xl font-bold text-white">{stats.total_creators}</div>
            <div className="text-sm text-slate-400">Creators</div>
          </button>
          <button onClick={() => setActiveTab('content')} className="glass-light rounded-xl p-4 text-left hover:ring-2 hover:ring-blue-500/30 transition-all">
            <FilmStrip className="w-8 h-8 text-blue-400 mb-2" />
            <div className="text-2xl font-bold text-white">{stats.total_content}</div>
            <div className="text-sm text-slate-400">User Content</div>
          </button>
          <button onClick={() => setActiveTab('aggregator')} className="glass-light rounded-xl p-4 text-left hover:ring-2 hover:ring-green-500/30 transition-all">
            <Database className="w-8 h-8 text-green-400 mb-2" />
            <div className="text-2xl font-bold text-white">{stats.total_external_content}</div>
            <div className="text-sm text-slate-400">External Content</div>
          </button>
          <div className="glass-light rounded-xl p-4">
            <CreditCard className="w-8 h-8 text-indigo-400 mb-2" />
            <div className="text-2xl font-bold text-white">{stats.active_subscriptions}</div>
            <div className="text-sm text-slate-400">Active Subs</div>
          </div>
          {modStats && (
            <button onClick={() => setActiveTab('moderation')} className="glass-light rounded-xl p-4 text-left hover:ring-2 hover:ring-amber-500/30 transition-all">
              <ShieldWarning className="w-8 h-8 text-amber-400 mb-2" />
              <div className="text-2xl font-bold text-white">{modStats.pending}</div>
              <div className="text-sm text-slate-400">Pending Reports</div>
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <HorizontalScroller className="mb-6" data-testid="admin-tab-strip">
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex-shrink-0 ${
            activeTab === 'content' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-content"
        >
          External Content
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2 rounded-full font-medium transition-colors ${
            activeTab === 'categories' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-categories"
        >
          Categories
        </button>
        <button
          onClick={() => { setActiveTab('users'); setUserRoleFilter(''); }}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'users' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-users"
        >
          <Users className="w-4 h-4" />
          Users
        </button>
        <button
          onClick={() => { setActiveTab('moderation'); }}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'moderation' ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-moderation"
        >
          <ShieldWarning className="w-4 h-4" />
          Moderation
          {modStats?.pending > 0 && (
            <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 text-xs font-bold rounded-full bg-red-500 text-white">
              {modStats.pending}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveTab('content-review'); fetchPendingContent(); }}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'content-review' ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-content-review"
        >
          <Eye className="w-4 h-4" />
          Content Review
          {pendingContentCount > 0 && (
            <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 text-xs font-bold rounded-full bg-indigo-600 text-white">
              {pendingContentCount}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveTab('aggregator'); fetchAggCategories(); fetchAutoStatus(); fetchContentSources(); }}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'aggregator' ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-aggregator"
        >
          <Robot className="w-4 h-4" />
          Aggregator
        </button>
        <button
          onClick={() => setActiveTab('affiliates')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'affiliates' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-affiliates"
        >
          <ShareNetwork className="w-4 h-4" />
          Affiliates
        </button>
        <button
          onClick={() => setActiveTab('health')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'health' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-health"
        >
          <Heartbeat className="w-4 h-4" />
          Health
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'payments' ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-payments"
        >
          <CreditCard className="w-4 h-4" />
          Payments
        </button>
        <button
          onClick={() => setActiveTab('deals')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'deals' ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-deals"
        >
          <Crown className="w-4 h-4" />
          Deals
        </button>
        <button
          onClick={() => setActiveTab('payouts')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'payouts' ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-payouts"
        >
          <CreditCard className="w-4 h-4" />
          Payouts
        </button>
        <button
          onClick={() => setActiveTab('broadcasts')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'broadcasts' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-broadcasts"
        >
          <Megaphone className="w-4 h-4" />
          Messages
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'feedback' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-feedback"
        >
          <ChatCenteredDots className="w-4 h-4" />
          Feedback
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'activity' ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          data-testid="tab-activity"
        >
          <ClockCounterClockwise className="w-4 h-4" />
          Activity
        </button>
      </HorizontalScroller>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div>
          {/* Actions Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search content..."
                className="w-full bg-slate-900 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSeedContent}
                className="px-4 py-3 bg-green-500/20 text-green-400 rounded-xl font-medium hover:bg-green-500/30 transition-colors flex items-center gap-2"
                data-testid="seed-content-btn"
              >
                <Database className="w-5 h-5" />
                Seed Sample Data
              </button>
              {/* Bulk Import Button */}
              <Dialog open={isImportOpen} onOpenChange={(open) => { setIsImportOpen(open); if (!open) { setImportData(''); setImportResult(null); } }}>
                <DialogTrigger asChild>
                  <button
                    className="px-4 py-3 bg-blue-500/20 text-blue-400 rounded-xl font-medium hover:bg-blue-500/30 transition-colors flex items-center gap-2"
                    data-testid="bulk-import-btn"
                  >
                    <UploadSimple className="w-5 h-5" />
                    Bulk Import
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="bulk-import-description">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Bulk Import Content</DialogTitle>
                    <p id="bulk-import-description" className="text-sm text-slate-400">Import external content in bulk using JSON or CSV format</p>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* Mode Toggle */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setImportMode('json')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          importMode === 'json' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                        }`}
                        data-testid="import-mode-json"
                      >
                        JSON
                      </button>
                      <button
                        onClick={() => setImportMode('csv')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          importMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                        }`}
                        data-testid="import-mode-csv"
                      >
                        CSV
                      </button>
                    </div>

                    {/* Format Example */}
                    <div className="bg-slate-800/50 border border-white/5 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {importMode === 'json' ? 'JSON Format Example:' : 'CSV Format Example:'}
                      </p>
                      <pre className="text-xs text-slate-400 overflow-x-auto">
{importMode === 'json' ? `[
  {
    "title": "Video Title",
    "thumbnail_url": "https://...",
    "source_url": "https://...",
    "source_name": "SourceName",
    "duration": "12:34",
    "views": 10000,
    "tags": ["tag1", "tag2"],
    "content_type": "video",
    "category": "amateur",
    "tier": "free"
  }
]` : `title,thumbnail_url,source_url,source_name,duration,views,tags,content_type,category,tier
"Video Title","https://...","https://...","SourceName","12:34",10000,"tag1,tag2","video","amateur","free"`}
                      </pre>
                    </div>

                    {/* Data Input */}
                    <div>
                      <Label>Paste your {importMode.toUpperCase()} data below</Label>
                      <Textarea
                        value={importData}
                        onChange={(e) => setImportData(e.target.value)}
                        className="bg-slate-800 border-white/10 mt-1 font-mono text-sm min-h-[200px]"
                        placeholder={importMode === 'json' ? '[{"title": "...", ...}]' : 'title,thumbnail_url,source_url,...'}
                        data-testid="import-data-input"
                      />
                    </div>

                    {/* Import Result */}
                    {importResult && (
                      <div className={`rounded-lg p-3 border ${
                        importResult.errors > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-green-500/10 border-green-500/30'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          {importResult.errors > 0 ? (
                            <Warning className="w-4 h-4 text-amber-400" />
                          ) : (
                            <Check className="w-4 h-4 text-green-400" />
                          )}
                          <span className="text-sm font-medium text-white">Import Results</span>
                        </div>
                        <div className="text-sm text-slate-300 space-y-1">
                          <p>Imported: <span className="text-green-400 font-medium">{importResult.imported}</span></p>
                          <p>Skipped (duplicates): <span className="text-slate-400">{importResult.skipped}</span></p>
                          {importResult.errors > 0 && (
                            <p>Errors: <span className="text-amber-400 font-medium">{importResult.errors}</span></p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => { setIsImportOpen(false); setImportData(''); setImportResult(null); }}
                        className="flex-1 py-3 border border-white/10 text-white rounded-full font-medium hover:bg-white/5"
                      >
                        Close
                      </button>
                      <button
                        onClick={handleBulkImport}
                        disabled={importing || !importData.trim()}
                        className="flex-1 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid="import-submit-btn"
                      >
                        {importing ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <UploadSimple className="w-5 h-5" />
                            Import
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Clear All Button */}
              <button
                onClick={handleClearAllContent}
                className="px-4 py-3 bg-red-500/10 text-red-400 rounded-xl font-medium hover:bg-red-500/20 transition-colors flex items-center gap-2"
                data-testid="clear-all-content-btn"
              >
                <Trash className="w-5 h-5" />
                Clear All
              </button>

              <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <button
                    className="px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
                    data-testid="add-content-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Add Content
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                      {editingItem ? 'Edit Content' : 'Add External Content'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="bg-slate-800 border-white/10 mt-1"
                        data-testid="content-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Thumbnail URL *</Label>
                        <Input
                          value={formData.thumbnail_url}
                          onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                          className="bg-slate-800 border-white/10 mt-1"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label>Source URL *</Label>
                        <Input
                          value={formData.source_url}
                          onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                          className="bg-slate-800 border-white/10 mt-1"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Source Name</Label>
                        <Input
                          value={formData.source_name}
                          onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
                          className="bg-slate-800 border-white/10 mt-1"
                          placeholder="e.g. ExampleTube"
                        />
                      </div>
                      <div>
                        <Label>Duration (videos)</Label>
                        <Input
                          value={formData.duration}
                          onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                          className="bg-slate-800 border-white/10 mt-1"
                          placeholder="e.g. 12:34"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Content Type</Label>
                        <select
                          value={formData.content_type}
                          onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                          className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
                        >
                          <option value="video">Video</option>
                          <option value="gallery">Gallery</option>
                        </select>
                      </div>
                      <div>
                        <Label>Category</Label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
                        >
                          {categories.length > 0 ? (
                            categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))
                          ) : (
                            <>
                              <option value="amateur">Amateur</option>
                              <option value="professional">Professional</option>
                              <option value="couples">Couples</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div>
                        <Label>Tier</Label>
                        <select
                          value={formData.tier}
                          onChange={(e) => setFormData({ ...formData, tier: e.target.value, is_free: e.target.value === 'free' })}
                          className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
                        >
                          <option value="free">Free</option>
                          <option value="basic">Basic</option>
                          <option value="premium">Premium</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label>Views</Label>
                      <Input
                        type="number"
                        value={formData.views}
                        onChange={(e) => setFormData({ ...formData, views: e.target.value })}
                        className="bg-slate-800 border-white/10 mt-1"
                      />
                    </div>
                    <div>
                      <Label>Tags (comma separated)</Label>
                      <Input
                        value={formData.tags}
                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                        className="bg-slate-800 border-white/10 mt-1"
                        placeholder="amateur, outdoor, fun"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => { setIsAddOpen(false); resetForm(); }}
                        className="flex-1 py-3 border border-white/10 text-white rounded-full font-medium hover:bg-white/5"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="flex-1 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600"
                        data-testid="save-content-btn"
                      >
                        {editingItem ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Content Table */}
          <div className="glass-light rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Content</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Source</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Tier</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Views</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContent.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-slate-500">
                        No content found. Click "Seed Sample Data" or "Add Content" to get started.
                      </td>
                    </tr>
                  ) : (
                    filteredContent.map((item, idx) => (
                      <tr key={item.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 cursor-pointer"
                               onClick={() => setGalleryIndex(idx)}
                               data-testid={`open-gallery-${item.id}`}>
                            <img
                              src={item.thumbnail_url}
                              alt={item.title}
                              className="w-16 h-12 rounded-lg object-cover bg-slate-800 hover:ring-2 hover:ring-blue-500/50 transition-all"
                            />
                            <div>
                              <p className="text-white font-medium line-clamp-1 hover:text-blue-300 transition-colors">{item.title}</p>
                              <p className="text-xs text-slate-500">{item.content_type} {item.duration && `• ${item.duration}`}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-400">{item.source_name}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded-full">
                            {item.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            item.tier === 'free' ? 'bg-green-500/20 text-green-400' :
                            item.tier === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {item.tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400">
                          {item.views ? `${(item.views / 1000).toFixed(0)}K` : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg"
                              data-testid={`edit-content-${item.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-500/10 rounded-lg"
                              data-testid={`delete-content-${item.id}`}
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && <CategoriesTab onChange={fetchCategories} />}


      {/* Moderation Tab */}
      {activeTab === 'moderation' && <ModerationTab onActionTaken={refreshModStats} />}

      {/* Content Review Tab (Pre-publication moderation) */}
      {/* Content Review Tab */}
      {activeTab === 'content-review' && <ContentReviewTab onCountChange={setPendingContentCount} />}

      {/* Aggregator Tab */}
      {activeTab === 'aggregator' && (
        <div>
          {/* ───── Compliance Sweep — bright red octagonal "stop sign" button ───── */}
          <div className="glass-light rounded-2xl p-6 mb-6 border-2 border-red-500/40">
            <div className="flex items-start gap-5 flex-col sm:flex-row sm:items-center">
              <button
                onClick={() => runComplianceSweep(true)}
                disabled={sweepRunning}
                data-testid="compliance-sweep-btn"
                className="group relative flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  width: '180px',
                  height: '180px',
                  clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                  background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 50%, #7f1d1d 100%)',
                  boxShadow: '0 0 30px rgba(239,68,68,0.6), inset 0 0 20px rgba(0,0,0,0.3)',
                  animation: sweepRunning ? 'none' : 'pulse 2s ease-in-out infinite',
                }}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <Warning weight="fill" className="w-9 h-9 mb-1 drop-shadow-lg" />
                  <div className="text-[11px] font-black uppercase tracking-widest leading-tight text-center px-2">
                    {sweepRunning ? 'Scanning…' : 'Compliance'}
                  </div>
                  <div className="text-[11px] font-black uppercase tracking-widest leading-tight text-center px-2">
                    {sweepRunning ? '' : 'Sweep'}
                  </div>
                  <div className="text-[8px] font-bold uppercase tracking-wider mt-1 opacity-80">
                    {sweepRunning ? '' : 'click to scan'}
                  </div>
                </div>
              </button>

              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                  <ShieldCheck weight="fill" className="w-5 h-5 text-red-400" />
                  Attribution Compliance Sweep
                </h3>
                <p className="text-sm text-slate-400 mt-1 leading-relaxed">
                  Scans all aggregated external content and identifies items missing required attribution
                  (original post URL, author handle, source instance, etc.). <strong className="text-slate-300">Click the octagon to dry-run first</strong> — you'll see the impact before anything is deleted. A second confirmation is required to actually delete. Repurposable for any future bulk cleanup we need.
                </p>
                <div className="mt-2 text-xs text-slate-600">
                  Endpoint: <code className="text-amber-400/80">POST /api/admin/external-content/sweep-unattributed</code>
                </div>
              </div>
            </div>
          </div>

          {/* Auto-Aggregation Status */}
          {autoStatus && (
            <div className="glass-light rounded-2xl p-6 mb-6" data-testid="auto-agg-status">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <ArrowsClockwise className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white">Auto-Aggregation</h3>
                    <p className="text-sm text-slate-400">Scheduled every {autoStatus.interval_hours} hours</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
                  Active
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-white">{autoStatus.total_reddit_content}</div>
                  <div className="text-xs text-slate-500">Reddit Items</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-white">{autoStatus.total_runs}</div>
                  <div className="text-xs text-slate-500">Total Runs</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-white">{autoStatus.interval_hours}h</div>
                  <div className="text-xs text-slate-500">Interval</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-white">{autoStatus.posts_per_sub}</div>
                  <div className="text-xs text-slate-500">Posts/Sub</div>
                </div>
              </div>
              {autoStatus.last_auto_run && (
                <div className="mt-4 text-xs text-slate-500">
                  Last auto-run: {autoStatus.last_auto_run.completed_at
                    ? new Date(autoStatus.last_auto_run.completed_at).toLocaleString()
                    : autoStatus.last_auto_run.status}
                  {autoStatus.last_auto_run.result && (
                    <span className="ml-2 text-green-400">
                      (+{autoStatus.last_auto_run.result.total_new} new)
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Content Sources Overview */}
          {contentSources && (
            <div className="glass-light rounded-2xl p-6 mb-6" data-testid="content-sources">
              <h3 className="font-heading text-lg font-bold text-white mb-4">Content Sources</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {contentSources.sources.map(source => (
                  <div key={source.id} className="bg-slate-800/50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-white">{source.count}</div>
                    <div className="text-xs text-slate-500">{source.name}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      source.type === 'auto' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {source.type}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-center text-sm text-slate-400">
                Total: <span className="text-white font-bold">{contentSources.total}</span> items
              </div>
            </div>
          )}

          {/* Mastodon Aggregator */}
          <div className="glass-light rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <Database className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-white">Mastodon/Fediverse</h3>
                  <p className="text-sm text-slate-400">Auto-fetch from NSFW Mastodon instances (no auth needed)</p>
                </div>
              </div>
              <button
                onClick={runMastodonAggregator}
                disabled={mastodonAggregating}
                className="px-4 py-2 bg-indigo-500 text-white rounded-lg font-bold hover:bg-indigo-600 disabled:opacity-50 flex items-center gap-2 transition-colors text-sm"
                data-testid="run-mastodon-btn"
              >
                {mastodonAggregating ? (
                  <><ArrowsClockwise className="w-4 h-4 animate-spin" /> Fetching...</>
                ) : (
                  <><ArrowsClockwise className="w-4 h-4" /> Run Now</>
                )}
              </button>
            </div>
            <div className="text-xs text-slate-500">
              Pulls from nsfw.social, thicc.horse across 12 hashtags. Included in auto-aggregation schedule.
            </div>
          </div>

          {/* Twitter/X Importer */}
          <div className="glass-light rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center">
                <FilmStrip className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Twitter/X Importer</h3>
                <p className="text-sm text-slate-400">Paste tweet URLs to import media (via FixTweet)</p>
              </div>
            </div>
            <div className="space-y-3">
              <Textarea
                value={twitterUrls}
                onChange={(e) => setTwitterUrls(e.target.value)}
                placeholder="Paste tweet URLs (one per line)&#10;https://x.com/user/status/12345&#10;https://twitter.com/user/status/67890"
                rows={4}
                className="w-full bg-slate-800 border border-white/10 rounded-xl text-white placeholder-slate-600 text-sm"
                data-testid="twitter-urls-input"
              />
              <div className="flex items-center gap-3">
                <select
                  value={twitterCategory}
                  onChange={(e) => setTwitterCategory(e.target.value)}
                  className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="amateur">Amateur</option>
                  <option value="professional">Professional</option>
                  <option value="glamour">Glamour</option>
                  <option value="cosplay">Cosplay</option>
                </select>
                <button
                  onClick={importTwitterContent}
                  disabled={twitterImporting || !twitterUrls.trim()}
                  className="px-4 py-2 bg-sky-500 text-white rounded-lg font-bold hover:bg-sky-600 disabled:opacity-50 flex items-center gap-2 transition-colors text-sm"
                  data-testid="import-twitter-btn"
                >
                  {twitterImporting ? (
                    <><ArrowsClockwise className="w-4 h-4 animate-spin" /> Importing...</>
                  ) : (
                    <><UploadSimple className="w-4 h-4" /> Import</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Manual Controls */}
          <div className="glass-light rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <RedditLogo weight="fill" className="w-8 h-8 text-orange-500" />
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Reddit Content Aggregator</h3>
                <p className="text-sm text-slate-400">Fetch free NSFW content links from Reddit communities</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Sort By</label>
                <select
                  value={aggSort}
                  onChange={(e) => setAggSort(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm"
                  data-testid="agg-sort"
                >
                  <option value="hot">Hot (Trending)</option>
                  <option value="new">New (Latest)</option>
                  <option value="top">Top (All Time)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Posts per Subreddit</label>
                <select
                  value={aggLimit}
                  onChange={(e) => setAggLimit(parseInt(e.target.value))}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm"
                  data-testid="agg-limit"
                >
                  <option value={10}>10 (Quick)</option>
                  <option value={25}>25 (Standard)</option>
                  <option value={50}>50 (Deep)</option>
                  <option value={100}>100 (Maximum)</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleRunAggregator}
                  disabled={aggregating}
                  className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-bold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                  data-testid="run-aggregator-btn"
                >
                  {aggregating ? (
                    <>
                      <ArrowsClockwise className="w-5 h-5 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Robot className="w-5 h-5" />
                      Run Aggregator
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Result */}
            {aggResult && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle weight="fill" className="w-5 h-5 text-green-400" />
                  <span className="font-semibold text-white">Aggregation Complete</span>
                </div>
                <div className="text-sm text-slate-300 space-y-1">
                  <p>New items imported: <span className="text-green-400 font-bold">{aggResult.new_items}</span></p>
                  <p>Duplicates skipped: <span className="text-slate-500">{aggResult.duplicates_skipped}</span></p>
                </div>
                {aggResult.by_category && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(aggResult.by_category).map(([cat, data]) => (
                      <span key={cat} className="px-2 py-1 bg-slate-800 rounded-full text-xs text-slate-300">
                        {cat}: <span className="text-green-400">+{data.new}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category Grid */}
          <h3 className="font-heading text-lg font-bold text-white mb-4">Source Categories</h3>
          {aggCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aggCategories.map(cat => (
                <div key={cat.category} className="glass-light rounded-xl p-4" data-testid={`agg-cat-${cat.category}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white capitalize">{cat.category}</h4>
                    <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs font-bold rounded-full">
                      {cat.content_count} items
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{cat.subreddit_count} subreddits</p>
                  <div className="flex flex-wrap gap-1">
                    {cat.subreddits.slice(0, 4).map(sub => (
                      <span key={sub} className="text-[10px] px-2 py-0.5 bg-slate-800 text-slate-400 rounded-full">
                        r/{sub}
                      </span>
                    ))}
                    {cat.subreddits.length > 4 && (
                      <span className="text-[10px] px-2 py-0.5 bg-slate-800 text-slate-500 rounded-full">
                        +{cat.subreddits.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-light rounded-2xl p-8 text-center">
              <Robot className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">Loading categories...</p>
            </div>
          )}
        </div>
      )}

      {/* Affiliates Tab */}
      {/* Affiliates Tab */}
      {activeTab === 'affiliates' && <AffiliatesTab />}

      {/* Health Monitor Tab */}
      {activeTab === 'health' && <HealthTab />}

      {/* Users Tab */}
      {activeTab === 'users' && <UsersTab roleFilter={userRoleFilter} setRoleFilter={setUserRoleFilter} fetchStats={fetchStats} />}

      {/* Payments Tab */}
      {activeTab === 'payments' && <PaymentsTab />}

      {/* Deals Tab */}
      {activeTab === 'deals' && <DealsTab />}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && <PayoutsTab />}

      {/* Broadcasts Tab */}
      {activeTab === 'broadcasts' && <BroadcastsTab />}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && <FeedbackAdminTab />}

      {activeTab === 'activity' && <ActivityLogTab />}

      {/* Admin Gallery Viewer */}
      <AdminGalleryViewer
        open={galleryIndex !== null}
        items={filteredContent}
        index={galleryIndex ?? 0}
        onClose={() => setGalleryIndex(null)}
        onChangeIndex={setGalleryIndex}
        onEdit={(item) => { setGalleryIndex(null); handleEdit(item); }}
        onDelete={async (item) => {
          await axios.delete(`${API}/api/admin/external-content/${item.id}`, { withCredentials: true });
          toast.success('Deleted');
          const res = await axios.get(`${API}/api/admin/external-content`, { withCredentials: true });
          setContent(res.data);
          if (filteredContent.length <= 1) setGalleryIndex(null);
          else setGalleryIndex(i => Math.min(i, filteredContent.length - 2));
        }}
      />

      {/* Compliance Sweep — dry-run confirm modal (mounted at root so it renders regardless of active tab) */}
      {sweepShowConfirm && (
        <SweepConfirmModal
          dryRun={sweepDryRun}
          running={sweepRunning}
          onCancel={() => { setSweepShowConfirm(false); setSweepDryRun(null); }}
          onFire={() => runComplianceSweep(false)}
        />
      )}
    </div>
  );
};

export default AdminPage;
